import React from 'react'
import axios from 'axios'
import { baseUrl } from '../../assets/Animations'
import { getRequestHeaders } from '../../api'
import { convertSchoolAbbreviationToFullName } from '../../Calendar/calendarFunctions'
import {
    buildInquirySmsPeriodOptions,
    canDeleteNoSmsRecordedRows,
    filterRecordedResponseRows,
    getInquirySmsPeriodWindow,
    getInquirySmsWeekWindow,
} from './inquirySmsResponseUtils'
import './inquiry-sms-response.css'

const formatEasternDateTime = (value) => {
    if (!value) return '—'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return '—'

    return new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
    }).format(date)
}

const formatWorkingMinutes = (value) => {
    if (value == null || Number.isNaN(Number(value))) return '—'
    const minutes = Math.max(0, Math.round(Number(value)))
    if (minutes < 60) return `${minutes}m`
    const hours = Math.floor(minutes / 60)
    const remainder = minutes % 60
    return remainder ? `${hours}h ${remainder}m` : `${hours}h`
}

const getAverageResponseTone = (value) => {
    if (value == null || Number.isNaN(Number(value))) return 'neutral'
    const minutes = Number(value)
    if (minutes < 15) return 'good'
    if (minutes < 30) return 'warning'
    return 'issue'
}

const formatSchool = (school) => {
    const value = String(school || '').trim()
    if (!value) return '—'
    return convertSchoolAbbreviationToFullName(value) || value.toUpperCase()
}

const MetricCard = ({ label, value, description, tone = 'neutral' }) => (
    <div className={`inquiry-sms-response__metric is-${tone}`}>
        <div className="inquiry-sms-response__metric-label">{label}</div>
        <strong>{value}</strong>
        <div className="inquiry-sms-response__metric-description">{description}</div>
    </div>
)

const StatusPill = ({ status }) => {
    const statusLabels = {
        on_time: 'On time',
        late: 'Late',
        pending: 'Pending',
        overdue_uncontacted: 'Overdue',
        not_sent: 'Not sent',
    }

    return (
        <span className={`inquiry-sms-response__status is-${status}`}>
            {statusLabels[status] || status}
        </span>
    )
}

const InquirySmsResponseSkeleton = () => (
    <div
        className="inquiry-sms-response__skeleton-overlay"
        role="status"
        aria-label="Loading SMS response reporting period"
    >
        <div className="inquiry-sms-response__skeleton-metrics">
            {[0, 1, 2, 3].map((index) => (
                <div key={index} className="inquiry-sms-response__skeleton-card">
                    <span className="inquiry-sms-response__skeleton-line is-label" />
                    <span className="inquiry-sms-response__skeleton-line is-value" />
                    <span className="inquiry-sms-response__skeleton-line is-description" />
                </div>
            ))}
        </div>
        <div className="inquiry-sms-response__skeleton-counts">
            {[0, 1, 2, 3, 4].map((index) => (
                <span key={index} className="inquiry-sms-response__skeleton-line" />
            ))}
        </div>
        <div className="inquiry-sms-response__skeleton-panels">
            {[0, 1, 2].map((index) => (
                <div
                    key={index}
                    className={`inquiry-sms-response__skeleton-panel${index === 2 ? ' is-wide' : ''}`}
                >
                    <span className="inquiry-sms-response__skeleton-line is-heading" />
                    {[0, 1, 2, 3].map((rowIndex) => (
                        <span key={rowIndex} className="inquiry-sms-response__skeleton-line is-row" />
                    ))}
                </div>
            ))}
        </div>
    </div>
)

const InquirySmsResponse = ({ userData }) => {
    const [summary, setSummary] = React.useState(null)
    const [loading, setLoading] = React.useState(true)
    const [refreshing, setRefreshing] = React.useState(false)
    const [error, setError] = React.useState('')
    const [notice, setNotice] = React.useState('')
    const [selectedPeriod, setSelectedPeriod] = React.useState(
        () => getInquirySmsWeekWindow().value
    )
    const [deletingFamilyIds, setDeletingFamilyIds] = React.useState(() => new Set())
    const [markingNotSentFamilyIds, setMarkingNotSentFamilyIds] = React.useState(() => new Set())
    const hasLoadedSummary = React.useRef(false)
    const canDeleteNoSmsRows = canDeleteNoSmsRecordedRows(userData)

    const loadSummary = React.useCallback(async ({ silent = false } = {}) => {
        if (silent || hasLoadedSummary.current) {
            setRefreshing(true)
        } else {
            setLoading(true)
        }
        setError('')

        try {
            const requestConfig = getRequestHeaders()
            const periodWindow = getInquirySmsPeriodWindow(selectedPeriod)
            const config = periodWindow
                ? {
                    ...requestConfig,
                    params: {
                        ...(requestConfig?.params || {}),
                        startAt: periodWindow.startAt.toISOString(),
                        endAt: periodWindow.endAt.toISOString(),
                    },
                }
                : requestConfig
            const response = await axios.get(
                `${baseUrl}/dashboard/inquiry-sms-response-summary`,
                config
            )
            setSummary(response?.data || null)
            hasLoadedSummary.current = true
        } catch (requestError) {
            console.error('Unable to load inquiry SMS response metrics:', requestError)
            setError(
                requestError?.response?.data?.message ||
                requestError?.message ||
                'Unable to load inquiry SMS response metrics.'
            )
        } finally {
            setLoading(false)
            setRefreshing(false)
        }
    }, [selectedPeriod])

    React.useEffect(() => {
        loadSummary()
        const refreshTimer = window.setInterval(
            () => loadSummary({ silent: true }),
            60 * 1000
        )
        return () => window.clearInterval(refreshTimer)
    }, [loadSummary])

    const markSmsNotSent = async (row) => {
        const familyId = String(row?.familyId || '').trim()
        if (!canDeleteNoSmsRows || !familyId || markingNotSentFamilyIds.has(familyId)) return

        const familyName = String(row?.familyName || 'this family').trim()
        const confirmed = window.confirm(
            `Mark the SMS for ${familyName} as not sent? This stops the response clock but keeps the inquiry in the report.`
        )
        if (!confirmed) return

        setMarkingNotSentFamilyIds((current) => new Set(current).add(familyId))
        setError('')
        setNotice('')
        try {
            const familyStatus = String(row?.familyStatus || '').trim()
            await axios.post(
                `${baseUrl}/dashboard/inquiry-sms-response-summary/${encodeURIComponent(familyId)}/not-sent`,
                {
                    reason: familyStatus
                        ? `Family was moved to ${familyStatus} before an SMS was sent.`
                        : 'SMS intentionally not sent.',
                },
                getRequestHeaders()
            )
            setNotice(`${familyName} was marked as not sent. The response clock has stopped.`)
            await loadSummary({ silent: true })
        } catch (requestError) {
            console.error('Unable to mark inquiry SMS as not sent:', requestError)
            setError(
                requestError?.response?.data?.message
                || requestError?.message
                || 'Unable to mark this inquiry SMS as not sent.'
            )
        } finally {
            setMarkingNotSentFamilyIds((current) => {
                const next = new Set(current)
                next.delete(familyId)
                return next
            })
        }
    }

    const deleteInvalidInquiryRow = async (row) => {
        const familyId = String(row?.familyId || '').trim()
        if (!canDeleteNoSmsRows || !familyId || deletingFamilyIds.has(familyId)) return

        const familyName = String(row?.familyName || 'this family').trim()
        const confirmed = window.confirm(
            `Delete ${familyName} from SMS response tracking as an invalid inquiry? This will not delete the family or onboarding card.`
        )
        if (!confirmed) return

        setDeletingFamilyIds((current) => new Set(current).add(familyId))
        setError('')
        setNotice('')
        try {
            await axios.delete(
                `${baseUrl}/dashboard/inquiry-sms-response-summary/${encodeURIComponent(familyId)}`,
                getRequestHeaders()
            )
            setNotice(`${familyName} was removed from SMS tracking as an invalid inquiry.`)
            await loadSummary({ silent: true })
        } catch (requestError) {
            console.error('Unable to remove inquiry from SMS response tracking:', requestError)
            setError(
                requestError?.response?.data?.message
                || requestError?.message
                || 'Unable to delete this invalid inquiry from SMS response tracking.'
            )
        } finally {
            setDeletingFamilyIds((current) => {
                const next = new Set(current)
                next.delete(familyId)
                return next
            })
        }
    }

    if (loading) {
        return (
            <div className="inquiry-sms-response__message">
                Loading SMS response tracking…
            </div>
        )
    }

    if (error && !summary) {
        return (
            <div className="inquiry-sms-response__message is-error" role="alert">
                <span>{error}</span>
                <button type="button" onClick={() => loadSummary()}>Retry</button>
            </div>
        )
    }

    const data = summary || {}
    const periodOptions = buildInquirySmsPeriodOptions(data.trackingStartAt)
    const rows = Array.isArray(data.rows) ? data.rows : []
    const uncontactedRows = Array.isArray(data.uncontactedRows)
        ? data.uncontactedRows
        : []
    const returnedContactedRows = Array.isArray(data.contactedRows)
        ? data.contactedRows
        : rows.filter((row) => row.responseAt)
    const contactedRows = filterRecordedResponseRows(returnedContactedRows)
    const notSentRows = Array.isArray(data.notSentRows) ? data.notSentRows : []
    const averageResponse = data.averageResponseMinutes == null
        ? '—'
        : formatWorkingMinutes(data.averageResponseMinutes)

    return (
        <div className="inquiry-sms-response">
            <div className="inquiry-sms-response__toolbar">
                <div>
                    {/* <p>
                        A successful SMS Template clipboard copy is the recorded response. The
                        clock runs 10:00 AM–6:00 PM Eastern on working days and pauses on weekends
                        and observed US federal holidays.
                    </p> */}
                    <span>
                        Tracking coverage begins {formatEasternDateTime(data.trackingStartAt)}.
                        Weekly views use the recorded SMS time; unanswered inquiries use the
                        business-hours response-clock start. The target is {data.responseTargetMinutes || 15} working minutes.
                    </span>
                </div>
                <div className="inquiry-sms-response__toolbar-actions">
                    <label htmlFor="inquiry-sms-response-period">
                        <span>Reporting period</span>
                        <select
                            id="inquiry-sms-response-period"
                            value={selectedPeriod}
                            onChange={(event) => setSelectedPeriod(event.target.value)}
                        >
                            {periodOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </label>
                    <button
                        type="button"
                        onClick={() => loadSummary({ silent: true })}
                        disabled={refreshing}
                    >
                        {refreshing ? 'Refreshing…' : 'Refresh'}
                    </button>
                </div>
            </div>

            {refreshing && <InquirySmsResponseSkeleton />}

            {error && (
                <div className="inquiry-sms-response__inline-error" role="alert">{error}</div>
            )}
            {notice && (
                <div className="inquiry-sms-response__inline-notice" role="status">{notice}</div>
            )}

            <div className="inquiry-sms-response__metrics">
                <MetricCard
                    label="On-time response rate"
                    value={`${Number(data.onTimeRate || 0).toFixed(1)}%`}
                    tone={Number(data.onTimeRate || 0) < 100 ? 'warning' : 'good'}
                    description={`${data.onTimeCount || 0} of ${data.dueInquiryCount || 0} inquiries currently due`}
                />
                <MetricCard
                    label="Average response time"
                    value={averageResponse}
                    tone={getAverageResponseTone(data.averageResponseMinutes)}
                    description="Average working time to the latest text in the initial session"
                />
                <MetricCard
                    label="Overdue · no text"
                    value={Number(data.overdueUncontacted || 0).toLocaleString()}
                    tone={data.overdueUncontacted ? 'issue' : 'good'}
                    description={`Past ${data.responseTargetMinutes || 15} working minutes with no text recorded`}
                />
                <MetricCard
                    label="Not sent"
                    value={Number(data.notSentCount || 0).toLocaleString()}
                    tone={Number(data.notSentCount || 0) > 0 ? 'issue' : 'good'}
                    description="Inquiries intentionally closed without an SMS"
                />
            </div>

            <div className="inquiry-sms-response__counts" aria-label="Inquiry response counts">
                <span><strong>{data.totalInquiries || 0}</strong> tracked inquiries</span>
                <span><strong>{data.contactedInquiries || 0}</strong> with a recorded text</span>
                <span><strong>{data.pendingUncontacted || 0}</strong> pending</span>
                <span><strong>{data.notSentCount || 0}</strong> marked not sent</span>
                <span><strong>{data.lateCount || 0}</strong> late responses</span>
                <span><strong>{data.followUpCopyAttempts || 0}</strong> follow-up texts</span>
            </div>

            <div className="inquiry-sms-response__grids">
                <section className="inquiry-sms-response__panel" aria-labelledby="uncontacted-inquiries-title">
                    <div className="inquiry-sms-response__panel-header">
                        <div>
                            <h4 id="uncontacted-inquiries-title">No SMS recorded</h4>
                            <p>
                                Mark valid inquiries that will not be texted, or delete invalid inquiries.
                            </p>
                        </div>
                        <strong>{data.noRecordedCopy || 0}</strong>
                    </div>
                    {uncontactedRows.length ? (
                        <div className="inquiry-sms-response__table-wrap">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Family</th>
                                        <th>School</th>
                                        <th>Students</th>
                                        <th>Received</th>
                                        <th>Working age</th>
                                        <th>Status</th>
                                        {canDeleteNoSmsRows && <th>Actions</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {uncontactedRows.map((row) => (
                                        <tr key={row.familyId}>
                                            <td>{row.familyName || 'Unnamed family'}</td>
                                            <td>{formatSchool(row.school)}</td>
                                            <td>{row.studentCount || 0}</td>
                                            <td>{formatEasternDateTime(row.receivedAt)}</td>
                                            <td>{formatWorkingMinutes(row.currentBusinessAgeMinutes)}</td>
                                            <td><StatusPill status={row.status} /></td>
                                            {canDeleteNoSmsRows && (
                                                <td className="inquiry-sms-response__actions-cell">
                                                    <button
                                                        type="button"
                                                        className="inquiry-sms-response__not-sent"
                                                        disabled={
                                                            markingNotSentFamilyIds.has(String(row.familyId))
                                                            || deletingFamilyIds.has(String(row.familyId))
                                                        }
                                                        onClick={() => markSmsNotSent(row)}
                                                    >
                                                        {markingNotSentFamilyIds.has(String(row.familyId))
                                                            ? 'Saving…'
                                                            : 'Mark not sent'}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="inquiry-sms-response__delete"
                                                        aria-label={`Delete ${row.familyName || 'family'} as an invalid inquiry`}
                                                        title="Delete invalid inquiry from SMS tracking"
                                                        disabled={
                                                            deletingFamilyIds.has(String(row.familyId))
                                                            || markingNotSentFamilyIds.has(String(row.familyId))
                                                        }
                                                        onClick={() => deleteInvalidInquiryRow(row)}
                                                    >
                                                        {deletingFamilyIds.has(String(row.familyId)) ? '…' : '×'}
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="inquiry-sms-response__empty">
                            No inquiries are currently waiting for an SMS outcome.
                        </div>
                    )}
                    <div className="inquiry-sms-response__panel-note">
                        “Mark not sent” stops the clock but retains the inquiry. The × removes an invalid inquiry from tracking.
                    </div>
                </section>

                <section className="inquiry-sms-response__panel" aria-labelledby="recorded-responses-title">
                    <div className="inquiry-sms-response__panel-header">
                        <div>
                            <h4 id="recorded-responses-title">Recorded responses</h4>
                            {/* <p>
                                The latest successful copy within the four-working-hour initial session sets the response time.
                            </p> */}
                        </div>
                        <strong>{contactedRows.length}</strong>
                    </div>
                    {contactedRows.length ? (
                        <div className="inquiry-sms-response__table-wrap">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Family</th>
                                        <th>Admin</th>
                                        <th>Response</th>
                                        <th>Attempts</th>
                                        <th>Follow-ups</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {contactedRows.map((row) => (
                                        <tr key={row.familyId}>
                                            <td>{row.familyName || 'Unnamed family'}</td>
                                            <td>{row.responseActorName || 'Unknown admin'}</td>
                                            <td>{formatWorkingMinutes(row.responseMinutes)}</td>
                                            <td>{row.attemptCount || 0}</td>
                                            <td>{row.followUpAttemptCount || 0}</td>
                                            <td><StatusPill status={row.status} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="inquiry-sms-response__empty">
                            No successful SMS-template copies have been recorded yet.
                        </div>
                    )}
                </section>

                <section className="inquiry-sms-response__panel inquiry-sms-response__panel--not-sent" aria-labelledby="not-sent-responses-title">
                    <div className="inquiry-sms-response__panel-header">
                        <div>
                            <h4 id="not-sent-responses-title">Marked not sent</h4>
                            <p>Valid inquiries intentionally closed without an SMS.</p>
                        </div>
                        <strong>{notSentRows.length}</strong>
                    </div>
                    {notSentRows.length ? (
                        <div className="inquiry-sms-response__table-wrap">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Family</th>
                                        <th>School</th>
                                        <th>Family status</th>
                                        <th>Marked by</th>
                                        <th>Marked at</th>
                                        <th>Clock stopped at</th>
                                        {canDeleteNoSmsRows && <th aria-label="Actions" />}
                                    </tr>
                                </thead>
                                <tbody>
                                    {notSentRows.map((row) => (
                                        <tr key={row.familyId}>
                                            <td>{row.familyName || 'Unnamed family'}</td>
                                            <td>{formatSchool(row.school)}</td>
                                            <td>{row.familyStatus || '—'}</td>
                                            <td>{row.notSentActorName || 'Unknown admin'}</td>
                                            <td>{formatEasternDateTime(row.notSentAt)}</td>
                                            <td>{formatWorkingMinutes(row.currentBusinessAgeMinutes)}</td>
                                            {canDeleteNoSmsRows && (
                                                <td className="inquiry-sms-response__delete-cell">
                                                    <button
                                                        type="button"
                                                        className="inquiry-sms-response__delete"
                                                        aria-label={`Delete ${row.familyName || 'family'} as an invalid inquiry`}
                                                        title="Delete invalid inquiry from SMS tracking"
                                                        disabled={deletingFamilyIds.has(String(row.familyId))}
                                                        onClick={() => deleteInvalidInquiryRow(row)}
                                                    >
                                                        {deletingFamilyIds.has(String(row.familyId)) ? '…' : '×'}
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="inquiry-sms-response__empty">
                            No inquiries have been marked as not sent.
                        </div>
                    )}
                </section>
            </div>
        </div>
    )
}

export default InquirySmsResponse
